<template>
    <purchase-contract-section>
        <template #title>
            <div class="flex w-full items-center justify-between">
                <div class="flex flex-col gap-1 md:flex-row">
                    <div>{{ t('purchaseContract.giftCardDetails') }}</div>
                    <div class="font-normal">
                        <span class="hidden md:inline">- </span
                        >{{ activeFacility.shortName }}
                    </div>
                </div>
                <div v-if="false">
                    <prime-button
                        :class="[
                            contractData !== undefined && !isActiveSection
                                ? ''
                                : 'invisible'
                        ]"
                        @click="emit('editSection')"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-pencil" />
                            <span class="lg:hidden">{{ t('edit') }}</span>
                            <span class="hidden lg:inline">{{
                                t('purchaseContract.changeOrder')
                            }}</span>
                        </div>
                    </prime-button>
                </div>
            </div>
        </template>

        <div
            v-if="!isActiveSection && contractData !== undefined"
            class="flex flex-wrap gap-x-16 gap-y-6 py-4"
        >
            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.giftCardPurchaseAmount')
                }}</label>
                <div>
                    {{ formattedSelectedPurchaseAmount }}
                </div>
            </div>

            <div v-if="showBonusPreview">
                <label class="font-semibold">{{
                    t('purchaseContract.giftCardBonus')
                }}</label>
                <div>
                    {{ formattedBonusAmount }}
                </div>
            </div>

            <div v-if="showBonusPreview">
                <label class="font-semibold">{{
                    t('purchaseContract.giftCardValue')
                }}</label>
                <div>
                    {{ formattedLoadedAmount }}
                </div>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.recipientName')
                }}</label>
                <div>
                    {{ formattedName }}
                </div>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.recipientEmail')
                }}</label>
                <div>
                    {{ contractData?.terms.giftCardTerms.email }}
                </div>
            </div>

            <div>
                <label class="font-semibold">{{ t('message') }}</label>
                <div
                    v-if="
                        (contractData?.terms.giftCardTerms.message ?? '') === ''
                    "
                    class="text-gray-400"
                >
                    &mdash;
                </div>
                <div v-else>
                    {{ contractData?.terms.giftCardTerms.message }}
                </div>
            </div>
        </div>

        <form
            v-if="isActiveSection"
            @submit="onSubmit"
        >
            <div class="space-y-1">
                <div class="flex flex-col items-center gap-6 md:flex-row">
                    <div class="flex flex-col">
                        {{ t('purchaseContract.giftCardAmount') }}
                        <prime-dropdown
                            v-model="amount"
                            v-bind="amountProps"
                            :options="amountList"
                            option-label="text"
                            option-value="key"
                            :placeholder="t('purchaseContract.selectAmount')"
                            class="w-full md:w-80"
                            :disabled="useCustom"
                        />
                        <div class="text-red-600 text-sm">
                            {{ (!useCustom && errors.amount) || '&nbsp;' }}
                        </div>
                    </div>

                    <div v-if="allowCustom">{{ t('purchaseContract.or') }}</div>

                    <div
                        v-if="allowCustom"
                        class="mb-6 space-y-2"
                    >
                        <div class="flex items-center gap-2">
                            <input-switch
                                v-model="useCustom"
                                input-id="gift-card-custom-amount"
                            />
                            <label for="gift-card-custom-amount">{{
                                t('purchaseContract.chooseCustomAmount')
                            }}</label>
                        </div>

                        <div
                            v-if="useCustom"
                            class="space-y-1"
                        >
                            <input-number
                                v-model="amount"
                                v-bind="amountProps"
                                input-id="customAmountInput"
                                placeholder="0.00"
                                mode="currency"
                                :currency="currencyFormat"
                                :locale="locale"
                                @focus="selectCustomAmountInput"
                            />
                            <div class="text-xs">
                                {{ customHint }}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="showBonusPreview"
                    class="max-w-xl rounded border border-green-200 bg-green-50 p-3 text-sm text-green-900"
                >
                    {{
                        t('purchaseContract.giftCardBonusSummary', {
                            purchaseAmount: formattedSelectedPurchaseAmount,
                            bonusAmount: formattedBonusAmount,
                            loadedAmount: formattedLoadedAmount
                        })
                    }}
                </div>

                <div class="flex flex-col">
                    {{ t('purchaseContract.recipientName') }}
                    <div class="flex flex-col gap-4 md:flex-row">
                        <div class="flex flex-col">
                            <input-text
                                v-model="firstName"
                                v-bind="firstNameProps"
                                :placeholder="
                                    t('customerInformation.firstName')
                                "
                                class="w-full md:w-80"
                            />
                            <div class="text-red-600 text-sm">
                                {{ errors.firstName || '&nbsp;' }}
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <input-text
                                v-model="lastName"
                                v-bind="lastNameProps"
                                :placeholder="t('customerInformation.lastName')"
                                class="w-full md:w-80"
                            />
                            <div class="text-red-600 text-sm">
                                {{ errors.lastName || '&nbsp;' }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col">
                    {{ t('purchaseContract.recipientEmail') }}
                    <input-text
                        v-model="email"
                        v-bind="emailProps"
                        inputmode="email"
                        type="email"
                        :placeholder="t('customerInformation.emailAddress')"
                        class="w-full md:w-80"
                    />
                    <div class="text-sm italic">
                        {{ t('purchaseContract.recipientEmailHelp') }}
                    </div>
                    <div class="text-red-600 text-sm">
                        {{ errors.email || '&nbsp;' }}
                    </div>
                </div>

                <div class="flex flex-col">
                    <div>
                        {{ t('purchaseContract.recipientMessage') }}
                        <span class="text-sm text-gray-500"
                            >({{ t('purchaseContract.optional') }})</span
                        >
                    </div>
                    <prime-textarea
                        v-model="message"
                        v-bind="messageProps"
                        :placeholder="
                            t('purchaseContract.recipientMessagePlaceholder')
                        "
                        class="w-full md:w-[30rem]"
                        maxlength="255"
                        rows="4"
                    />
                    <div class="text-red-600 text-sm">
                        {{ errors.message || '&nbsp;' }}
                    </div>
                </div>
            </div>

            <div class="mt-6 flex gap-4">
                <prime-button
                    v-if="contractData === undefined"
                    :label="t('continue')"
                    type="submit"
                    name="submitType"
                    value="continue"
                    :loading="isMutating"
                />

                <prime-button
                    v-else
                    :label="t('continue')"
                    :loading="isMutating"
                    type="submit"
                    name="submitType"
                    value="change"
                />
            </div>
        </form>
    </purchase-contract-section>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const SelectGiftCardContractFragment = graphql(/** GraphQL */ `
    fragment SelectGiftCardContractFragment on PlanContract {
        id
        updatedAtUtc
        startDateLocal
        endDateLocal
        facility {
            shortName
        }
        totalPrice
        terms {
            giftCardTerms {
                firstName
                lastName
                email
                message
            }
        }
    }
`)

export const SelectGiftCardPlanFragment = graphql(/** GraphQL */ `
    fragment SelectGiftCardPlanFragment on Plan {
        planType
        terms {
            allowGuestCheckout
        }
        giftCardOptions(input: $input) {
            amountList
            allowCustomAmounts
            minCustomAmount
            maxCustomAmount
            bonusRules {
                minimumPurchaseAmount
                bonusType
                bonusAmount
            }
        }
        sessions(facilityId: $facilityId) {
            id
            sessionFacilityHash
        }
    }
`)
</script>

<script setup lang="ts">
import { useShowLoginModal, useViewer } from '~/composables/use-login'
import PrimeButton from 'primevue/button'
import { ref, computed, watch, nextTick } from 'vue'
import { useActiveFacility, useLocalization, useStorefront } from '#imports'
import {
    parseLocal,
    toLocalIsoString
} from '~/composables/support/localization'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeDropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import PrimeTextarea from 'primevue/textarea'
import type {
    ChangeContractInput,
    CreateGiftCardInput
} from '~/resources/graphql/graphql'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import { useForm } from 'vee-validate'
import { object, string } from 'yup'
import { toTypedSchema } from '@vee-validate/yup'
import { Decimal } from '~/composables/support/decimal'
import InputSwitch from 'primevue/inputswitch'
import InputNumber from 'primevue/inputnumber'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    plan: FragmentType<typeof SelectGiftCardPlanFragment>
    contract?: FragmentType<typeof SelectGiftCardContractFragment>
    isActiveSection: boolean
    isMutating: boolean
}>()

const emit = defineEmits<{
    (e: 'createContract', payload: CreateGiftCardInput): void
    (e: 'changeContract', payload: ChangeContractInput): void
    (e: 'editSection'): void
}>()

const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })

const contractData = computed(() =>
    props.contract === undefined
        ? props.contract
        : getFragmentData(SelectGiftCardContractFragment, props.contract)
)

const planData = computed(() =>
    getFragmentData(SelectGiftCardPlanFragment, props.plan)
)

const amountList = computed(() =>
    planData.value.giftCardOptions.amountList.map(amount => ({
        key: amount,
        text: new Decimal(amount).formatCurrency()
    }))
)

const allowCustom = computed(
    () => planData.value.giftCardOptions.allowCustomAmounts
)

const customHint = computed(() => {
    const min = new Decimal(planData.value.giftCardOptions.minCustomAmount)
    const max = new Decimal(planData.value.giftCardOptions.maxCustomAmount)

    return min.formatCurrency() + ' - ' + max.formatCurrency()
})

const useCustom = ref(false)
watch(useCustom, newValue => {
    amount.value = ''

    if (newValue) {
        nextTick(() => document.getElementById('customAmountInput')?.focus())
    }
})

const schema = toTypedSchema(
    object({
        amount: string().required().label(t('purchaseContract.giftCardAmount')),
        firstName: string()
            .required()
            .min(2)
            .max(255)
            .label(t('purchaseContract.recipientFirstName')),
        lastName: string()
            .required()
            .min(2)
            .max(255)
            .label(t('purchaseContract.recipientLastName')),
        email: string()
            .required()
            .label(t('purchaseContract.recipientEmail'))
            .email(t('purchaseContract.validRecipientEmail')),
        message: string().max(255).label(t('purchaseContract.recipientMessage'))
    })
)

const { errors, defineField, handleSubmit } = useForm({
    validationSchema: schema
})

const [amount, amountProps] = defineField('amount', {
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false
})
const [firstName, firstNameProps] = defineField('firstName', {
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false
})
const [lastName, lastNameProps] = defineField('lastName', {
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false
})
const [email, emailProps] = defineField('email', {
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false
})
const [message, messageProps] = defineField('message', {
    validateOnBlur: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false
})

// One session for gift cards
const session = ref(planData.value.sessions[0])
const contractStartDate = ref<string | null>(null)
const contractEndDate = ref<string | null>(null)

const storefront = useStorefront()
const allowGuestCheckout = computed<boolean>(
    () =>
        storefront.value.allowGuestCheckout &&
        planData.value.terms.allowGuestCheckout
)

const viewer = useViewer()
const showLoginModal = useShowLoginModal()
const onSubmit = handleSubmit(async values => {
    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: allowGuestCheckout.value
        })
        if (customerId === undefined) {
            return
        }
    }

    if (contractData.value === undefined) {
        emit('createContract', {
            customerId,
            sessionFacilityHash: session.value.sessionFacilityHash,
            amount: values.amount,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            message: values.message ?? null
        })
    } else {
        const amountFormatted = new Decimal('').formatCurrency()
        emit('changeContract', {
            id: contractData.value.id,
            updatedAt: contractData.value.updatedAtUtc,
            // Rename startSessionFacilityHash
            startSessionFacilityHash: session.value.sessionFacilityHash,
            startDate: contractStartDate.value,
            endDate: contractEndDate.value,
            // for the purpose of reserving slots
            enrollmentTypeCounts: [],
            subtotal: amountFormatted,
            total: amountFormatted
        })
    }
})

watch(
    contractData,
    newValue => {
        if (newValue === undefined) {
            return
        }

        // TODO: Set the amount and such

        contractStartDate.value = toLocalIsoString(
            parseLocal(newValue.startDateLocal)
        )
    },
    { immediate: true }
)

const selectedPurchaseAmount = computed(() => {
    const selectedAmount = contractData.value?.totalPrice ?? amount.value ?? '0'

    if (selectedAmount === '') {
        return Decimal.zero()
    }

    return new Decimal(String(selectedAmount))
})

const selectedBonusAmount = computed(() => {
    const matchedRule = [...planData.value.giftCardOptions.bonusRules]
        .filter(rule =>
            selectedPurchaseAmount.value.isGreaterThanOrEqualTo(
                new Decimal(rule.minimumPurchaseAmount)
            )
        )
        .sort((a, b) => {
            const aMinimum = new Decimal(a.minimumPurchaseAmount)
            const bMinimum = new Decimal(b.minimumPurchaseAmount)

            if (aMinimum.isGreaterThan(bMinimum)) {
                return -1
            }

            if (aMinimum.isLessThan(bMinimum)) {
                return 1
            }

            return 0
        })[0]

    if (matchedRule === undefined) {
        return Decimal.zero()
    }

    if (matchedRule.bonusType === 'percent') {
        return selectedPurchaseAmount.value
            .multipliedBy(new Decimal(matchedRule.bonusAmount))
            .dividedBy(new Decimal('100'))
            .decimalPlaces(2)
    }

    return new Decimal(matchedRule.bonusAmount).decimalPlaces(2)
})

const selectedLoadedAmount = computed(() =>
    selectedPurchaseAmount.value.plus(selectedBonusAmount.value)
)

const showBonusPreview = computed(() =>
    selectedBonusAmount.value.isPositiveAndNotZero()
)

const formattedSelectedPurchaseAmount = computed(() => {
    return selectedPurchaseAmount.value.formatCurrency()
})

const formattedBonusAmount = computed(() => {
    return selectedBonusAmount.value.formatCurrency()
})

const formattedLoadedAmount = computed(() => {
    return selectedLoadedAmount.value.formatCurrency()
})

const formattedName = computed(() => {
    if (contractData.value === undefined) {
        return ''
    }

    return (
        contractData.value.terms.giftCardTerms.firstName +
        ' ' +
        contractData.value.terms.giftCardTerms.lastName
    )
})

function selectCustomAmountInput() {
    document.getElementById('customAmountInput')?.select()
}

const { currencyFormat, locale } = useLocalization()
</script>
